import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
    Calendar, Clock, MapPin, Users, Coins, 
    Sparkles, Heart, Palette, Music, Compass,
    Check, X, AlertTriangle, ChevronDown, ChevronUp, CalendarPlus, Download
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
    move_well: { icon: Heart, label: 'Move Well', gradient: 'from-emerald-500 to-teal-600' },
    connect_well: { icon: Users, label: 'Connect Well', gradient: 'from-blue-500 to-indigo-600' },
    create_well: { icon: Palette, label: 'Create Well', gradient: 'from-purple-500 to-pink-600' },
    feel_well: { icon: Sparkles, label: 'Feel Well', gradient: 'from-amber-500 to-orange-600' },
    explore_well: { icon: Compass, label: 'Explore Well', gradient: 'from-cyan-500 to-blue-600' },
    celebrate_well: { icon: Music, label: 'Celebrate Well', gradient: 'from-rose-500 to-red-600' }
};

function BookingCard({ booking, onCancel, token }) {
    const activity = booking.activity;
    if (!activity) return null;
    
    const config = CATEGORY_CONFIG[activity.category] || CATEGORY_CONFIG.connect_well;
    const Icon = config.icon;
    const activityDate = new Date(activity.date_time);
    const now = new Date();
    const isPast = activityDate < now;
    const hoursUntil = (activityDate - now) / (1000 * 60 * 60);
    const canCancel = !isPast && hoursUntil >= 24;
    const isWaitlist = booking.status === 'waitlist';
    const isCancelled = booking.status === 'cancelled';
    
    const handleAddToCalendar = () => {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/booking/${booking.id}.ics`, '_blank');
    };
    
    return (
        <div 
            className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${
                isCancelled ? 'border-stone-200 opacity-60' : 
                isWaitlist ? 'border-amber-200' : 
                'border-stone-200'
            }`}
            data-testid={`booking-card-${booking.id}`}
        >
            <div className={`h-2 bg-gradient-to-r ${config.gradient} ${isCancelled ? 'opacity-30' : ''}`} />
            <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center ${isCancelled ? 'opacity-30' : ''}`}>
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h4 className={`font-bold ${isCancelled ? 'text-stone-400 line-through' : 'text-stone-900'}`}>
                                {activity.title}
                            </h4>
                            <span className={`text-xs font-medium ${
                                isCancelled ? 'text-stone-400' :
                                isWaitlist ? 'text-amber-600' :
                                isPast ? 'text-stone-500' :
                                'text-emerald-600'
                            }`}>
                                {isCancelled ? 'Cancelled' :
                                 isWaitlist ? 'On Waitlist' :
                                 isPast ? 'Completed' :
                                 'Confirmed'}
                            </span>
                        </div>
                    </div>
                    
                    {booking.credits_used > 0 && !isCancelled && (
                        <span className="flex items-center gap-1 text-amber-600 font-medium text-sm">
                            <Coins className="w-4 h-4" />
                            {booking.credits_used}
                        </span>
                    )}
                </div>
                
                <div className="space-y-1.5 text-sm text-stone-500 mb-4">
                    <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{activityDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{activityDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{activity.location}</span>
                    </div>
                </div>
                
                {!isCancelled && !isPast && (
                    <div className="pt-4 border-t border-stone-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {canCancel ? (
                                <button
                                    onClick={() => onCancel(booking)}
                                    className="text-red-600 text-sm font-medium flex items-center gap-1 hover:text-red-700"
                                >
                                    <X className="w-4 h-4" />
                                    Cancel
                                </button>
                            ) : (
                                <p className="text-xs text-stone-400 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Can't cancel within 24h
                                </p>
                            )}
                        </div>
                        <button
                            onClick={handleAddToCalendar}
                            className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
                        >
                            <CalendarPlus className="w-4 h-4" />
                            Add to Calendar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export function MyBookingsPage() {
    const navigate = useNavigate();
    const { token } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPast, setShowPast] = useState(false);
    const [cancelModal, setCancelModal] = useState({ open: false, booking: null });
    const [cancelling, setCancelling] = useState(false);

    const fetchBookings = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/member/bookings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBookings(response.data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchBookings();
        }
    }, [token, fetchBookings]);

    const handleCancel = (booking) => {
        setCancelModal({ open: true, booking });
    };

    const confirmCancel = async () => {
        if (!cancelModal.booking) return;
        
        setCancelling(true);
        try {
            const response = await axios.delete(
                `${API}/bookings/${cancelModal.booking.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const refunded = response.data.credits_refunded;
            if (refunded > 0) {
                toast.success(`Booking cancelled. ${refunded} credits refunded.`);
            } else {
                toast.success('Booking cancelled');
            }
            
            setCancelModal({ open: false, booking: null });
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to cancel booking');
        } finally {
            setCancelling(false);
        }
    };

    const now = new Date();
    const upcomingBookings = bookings.filter(b => {
        const activityDate = b.activity ? new Date(b.activity.date_time) : null;
        return activityDate && activityDate >= now && b.status !== 'cancelled';
    }).sort((a, b) => new Date(a.activity?.date_time) - new Date(b.activity?.date_time));
    
    const pastBookings = bookings.filter(b => {
        const activityDate = b.activity ? new Date(b.activity.date_time) : null;
        return (activityDate && activityDate < now) || b.status === 'cancelled';
    }).sort((a, b) => new Date(b.activity?.date_time) - new Date(a.activity?.date_time));

    return (
        <div className="min-h-screen bg-stone-50" data-testid="my-bookings-page">
            <PageHeader title="My Bookings" showBack />
            
            <div className="px-5 py-6 pb-24">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : bookings.length === 0 ? (
                    <div className="text-center py-12">
                        <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-stone-900">No bookings yet</h3>
                        <p className="text-stone-500 mt-1 mb-6">Browse activities and book your first experience</p>
                        <Button
                            onClick={() => navigate('/residents/activities')}
                            className="bg-emerald-700 hover:bg-emerald-800"
                        >
                            Browse Activities
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Upcoming Bookings */}
                        <div className="mb-8">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-stone-900">
                                    Upcoming ({upcomingBookings.length})
                                </h2>
                                {upcomingBookings.length > 0 && (
                                    <button
                                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/my-bookings.ics`, '_blank')}
                                        className="text-emerald-700 text-sm font-medium flex items-center gap-1 hover:text-emerald-800"
                                    >
                                        <Download className="w-4 h-4" />
                                        Export All
                                    </button>
                                )}
                            </div>
                            
                            {upcomingBookings.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center">
                                    <p className="text-stone-500">No upcoming bookings</p>
                                    <button
                                        onClick={() => navigate('/residents/activities')}
                                        className="text-emerald-700 font-medium mt-2"
                                    >
                                        Find an activity →
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4" data-testid="upcoming-bookings">
                                    {upcomingBookings.map((booking) => (
                                        <BookingCard
                                            key={booking.id}
                                            booking={booking}
                                            onCancel={handleCancel}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Past Bookings */}
                        {pastBookings.length > 0 && (
                            <div>
                                <button
                                    onClick={() => setShowPast(!showPast)}
                                    className="flex items-center gap-2 text-stone-600 font-medium mb-4"
                                >
                                    Past & Cancelled ({pastBookings.length})
                                    {showPast ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </button>
                                
                                {showPast && (
                                    <div className="space-y-4" data-testid="past-bookings">
                                        {pastBookings.map((booking) => (
                                            <BookingCard
                                                key={booking.id}
                                                booking={booking}
                                                onCancel={handleCancel}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            
            {/* Cancel Confirmation Modal */}
            <Dialog open={cancelModal.open} onOpenChange={(open) => !open && setCancelModal({ open: false, booking: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Cancel Booking</DialogTitle>
                    </DialogHeader>
                    
                    {cancelModal.booking && (
                        <div className="space-y-4">
                            <p className="text-stone-600">
                                Are you sure you want to cancel your booking for <strong>{cancelModal.booking.activity?.title}</strong>?
                            </p>
                            
                            {cancelModal.booking.credits_used > 0 && (
                                <div className="bg-emerald-50 rounded-xl p-4 flex items-center gap-3">
                                    <Check className="w-5 h-5 text-emerald-600" />
                                    <span className="text-emerald-800">
                                        <strong>{cancelModal.booking.credits_used} credits</strong> will be refunded
                                    </span>
                                </div>
                            )}
                            
                            <div className="flex gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => setCancelModal({ open: false, booking: null })}
                                    className="flex-1"
                                >
                                    Keep Booking
                                </Button>
                                <Button
                                    onClick={confirmCancel}
                                    disabled={cancelling}
                                    variant="destructive"
                                    className="flex-1"
                                    data-testid="confirm-cancel-btn"
                                >
                                    {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default MyBookingsPage;
